import DOMPurify from 'dompurify';
import { isSafeURL } from './url-validation';

/**
 * Validate an array of DID records
 * @param {Array<Object>} records - Array of DID records to validate
 * @param {Array<string>} existingDIDs - Array of existing DIDs in the database
 * @returns {Array<Object>} - Array of validation errors
 */
export const validateRecords = (records, existingDIDs = []) => {
  const errors = [];
  const didsInFile = new Set();

  records.forEach((record, index) => {
    const rowNumber = index + 2; // +2 because row 1 is header, and index is 0-based

    // Validate required fields
    if (!record.did || record.did.trim() === '') {
      errors.push({
        row: rowNumber,
        column: 'DID',
        message: 'DID is required',
        value: record.did || ''
      });
    }

    if (!record.title || record.title.trim() === '') {
      errors.push({
        row: rowNumber,
        column: 'Title',
        message: 'Title is required',
        value: record.title || ''
      });
    }

    if (!record.location || record.location.trim() === '') {
      errors.push({
        row: rowNumber,
        column: 'Location/URL',
        message: 'Location/URL is required',
        value: record.location || ''
      });
    }

    if (!record.owner || record.owner.trim() === '') {
      errors.push({
        row: rowNumber,
        column: 'Owner',
        message: 'Owner is required',
        value: record.owner || ''
      });
    }

    if (!record.status || record.status.trim() === '') {
      errors.push({
        row: rowNumber,
        column: 'Status',
        message: 'Status is required',
        value: record.status || ''
      });
    }

    // Check DID uniqueness within file
    if (record.did && record.did.trim() !== '') {
      const normalizedDID = record.did.trim();
      if (didsInFile.has(normalizedDID)) {
        errors.push({
          row: rowNumber,
          column: 'DID',
          message: 'Duplicate DID within file',
          value: normalizedDID
        });
      } else {
        didsInFile.add(normalizedDID);
      }

      // Check DID uniqueness against existing data
      if (existingDIDs.includes(normalizedDID)) {
        errors.push({
          row: rowNumber,
          column: 'DID',
          message: 'DID already exists in database',
          value: normalizedDID
        });
      }
    }

    // Validate URL format for Location/URL field
    if (record.location && record.location.trim() !== '') {
      if (!isValidURL(record.location.trim())) {
        errors.push({
          row: rowNumber,
          column: 'Location/URL',
          message: 'Invalid URL format',
          value: record.location
        });
      }
    }
  });

  return errors;
};

/**
 * Validate URL format
 * Uses the url-validation utility for consistent URL validation across the application
 * @param {string} url - URL string to validate
 * @returns {boolean} - True if valid URL format
 */
const isValidURL = (url) => {
  return isSafeURL(url);
};

/**
 * Validate URL for navigation safety
 * Checks if a URL is safe to use for navigation (window.location assignment)
 * by validating the protocol and structure to prevent XSS attacks
 * @param {string} url - URL to validate for navigation
 * @returns {boolean} - True if URL is safe for navigation
 */
export const isNavigationSafe = (url) => {
  return isSafeURL(url);
};

/**
 * Validate AWS userid format
 * AWS userids are typically lowercase alphanumeric with hyphens, 1-64 characters
 * Examples: jdoe, john-doe, jdoe123
 * @param {string} userid - AWS userid to validate
 * @returns {boolean} - True if valid AWS userid format
 */
export const isValidAWSUserid = (userid) => {
  if (!userid || typeof userid !== 'string') {
    return false;
  }
  
  // AWS userid pattern: lowercase letters, numbers, hyphens, periods, underscores
  // Length: 1-64 characters
  const awsUseridPattern = /^[a-z0-9._-]{1,64}$/;
  return awsUseridPattern.test(userid.trim());
};

/**
 * Validate Approval ID format
 * Must be numeric only, between 6 and 10 digits
 * @param {string} approvalId - Approval ID to validate
 * @returns {boolean} - True if valid approval ID format
 */
export const isValidApprovalId = (approvalId) => {
  if (!approvalId || typeof approvalId !== 'string') {
    return false;
  }
  const trimmed = approvalId.trim();
  return /^\d{6,10}$/.test(trimmed);
};

/**
 * Sanitize text to prevent XSS attacks
 * @param {string} text - Text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * Sanitize a DID record object
 * @param {Object} record - DID record to sanitize
 * @returns {Object} - Sanitized record
 */
export const sanitizeRecord = (record) => {
  return {
    did: sanitizeText(record.did),
    title: sanitizeText(record.title),
    location: sanitizeText(record.location),
    filename: sanitizeText(record.filename),
    owner: sanitizeText(record.owner),
    status: sanitizeText(record.status)
  };
};

/**
 * Sanitize an array of records
 * @param {Array<Object>} records - Array of records to sanitize
 * @returns {Array<Object>} - Array of sanitized records
 */
export const sanitizeRecords = (records) => {
  if (!Array.isArray(records)) {
    return [];
  }
  return records.map(record => sanitizeRecord(record));
};
